import { useSuspenseQuery } from '@apollo/client';
import React, { useMemo } from 'react';

import { ColDefBuilder, DataTable } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';

import { SessionTypesActionCell } from './SessionTypesActionCell';
import { sessionTypesFragment } from './session-type.operations';

const sessionTypesQuery = graphql(
  `
    query getTenantSessionTypes($tenantId: uuid!) {
      tenantsByPk(id: $tenantId) {
        ...SessionTypes
      }
    }
  `,
  [sessionTypesFragment],
);

type Row = {
  tenantId: string;
  id: string;
  name: string;
  context?: string;
  isInitialVisitType: boolean;
};

export interface SessionTypesTableProps {
  tenantId: string;
}

export const SessionTypesTable: React.FC<SessionTypesTableProps> = ({
  tenantId,
}) => {
  const { t } = useTranslation();
  const {
    data: { tenantsByPk: tenant },
  } = useSuspenseQuery(sessionTypesQuery, { variables: { tenantId } });

  const columns = useMemo(
    () =>
      new ColDefBuilder<Row>()
        .detailsLink((r) => `${r.id}`, t('tenantAdmin.common.detailsColumn'))
        .defaultSortable('name', t('tenantAdmin.common.nameColumn'))
        .defaultSortable('context', t('tenantAdmin.sessionTypes.contextColumn'))
        .defaultBoolean(
          'isInitialVisitType',
          t('tenantAdmin.sessionTypes.isInitialVisitColumn'),
        )
        .colDef({
          header: t('tenantAdmin.common.actionsColumn'),
          cell: ({
            row: {
              original: { id, tenantId },
            },
          }) => {
            return <SessionTypesActionCell tenantId={tenantId} id={id} />;
          },
        })
        .build(),
    [t],
  );

  const data = useMemo(() => {
    return (tenant?.session_types ?? []).map<Row>((st) => {
      return {
        tenantId,
        id: st.id,
        name: st.name,
        context: st.context ?? undefined,
        isInitialVisitType: st.isInitialVisitType,
      };
    });
  }, [tenant, tenantId]);

  return <DataTable enableGlobalSearch={true} columns={columns} data={data} />;
};
